import React from 'react';
import { Button } from '../ui/button';
import { EditIcon, Trash2, Database } from 'lucide-react'; // Import EditIcon, Trash2, and Database

/**
 * Renders the list of data sources in the sidebar.
 * 
 * @param {object} props - Component props.
 * @param {Array} props.dataSources - Array of data source objects to display.
 * @param {Function} props.handleEditDataSource - Function to call when edit is clicked.
 * @param {Function} props.handleDeleteDataSourceClick - Function to call when delete is clicked.
 * @param {Function} props.handleViewSchema - Function to call when view schema is clicked (for database sources).
 */
export default function DataSourceList({ dataSources, handleEditDataSource, handleDeleteDataSourceClick, handleViewSchema }) {
    if (!dataSources || dataSources.length === 0) {
        return <p className="text-sm text-gray-600">No data sources added yet.</p>;
    }

    return (
        <ul className="space-y-2">
            {dataSources.map((ds, index) => {
                // Support both camelCase and snake_case attributes for flexibility
                const dataSourceId = ds.data_source_id || ds.DataSourceId; // Get the actual ID
                if (!dataSourceId) {
                    console.warn("DataSource missing ID at index", index, ds);
                    return null; // Skip rendering if ID is missing
                }
                const dataSourceType = ds.data_source_type || ds.DataSourceType;
                const dsName = ds.name || ds.Name;
                const dsDbName = ds.db_name || ds.DBName;
                const dsEndpoint = ds.endpoint || ds.Endpoint;
                const dsDbType = ds.db_type || ds.DBType;
                const dsDbHost = ds.db_host || ds.DBHost;

                const displayName = dsName || dsDbName || 'Unnamed Data Source';
                const displayType = dataSourceType === 'API' ? 'API' : 'DB';
                let displayDetails = 'Details unavailable';
                if (dataSourceType === 'API') {
                    displayDetails = dsEndpoint || 'No endpoint specified';
                } else {
                    displayDetails = `${dsDbType || 'Unknown DB Type'} on ${dsDbHost || 'unknown host'}`;
                }

                return (
                    <li key={dataSourceId} className="bg-white p-2 border rounded-md shadow-sm text-sm break-words flex justify-between items-center">
                        <div className="flex-grow overflow-hidden mr-2"> {/* Container for text content */}
                            <p className="font-medium text-gray-700 truncate" title={displayName}>
                                {displayType}: {displayName}
                            </p>
                            <p className="text-xs text-gray-500 truncate" title={displayDetails}>
                                {displayDetails}
                            </p>
                        </div>
                        <div className="flex-shrink-0 flex space-x-1"> {/* Container for buttons */}
                            {/* View Schema Button (for Database data sources only) */}
                            {dataSourceType === 'DATABASE' && handleViewSchema && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-green-600 hover:text-green-800"
                                    onClick={() => handleViewSchema(ds)}
                                    title="View Database Schema"
                                >
                                    <Database className="h-4 w-4" />
                                </Button>
                            )}
                            {/* Edit Button (for both APIs and Databases) */}
                            {(dataSourceType === 'API' || dataSourceType === 'DATABASE') && handleEditDataSource && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-blue-600 hover:text-blue-800"
                                    onClick={() => handleEditDataSource(ds)}
                                    title={`Edit ${dataSourceType === 'API' ? 'API' : 'Database'} Data Source`}
                                >
                                    <EditIcon className="h-4 w-4" />
                                </Button>
                            )}
                            {/* Delete Button (for all types) */}
                            {handleDeleteDataSourceClick && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-red-600 hover:text-red-800"
                                    onClick={() => handleDeleteDataSourceClick(dataSourceId)} // Pass the ID
                                    title="Delete Data Source"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}